import { motion } from 'framer-motion'
import { 
  Zap, 
  Layers, 
  Globe, 
  Shield, 
  Cpu, 
  Sparkles,
  Gauge,
  Cloud
} from 'lucide-react'

const features = [
  {
    icon: Zap,
    title: 'Real-time Performance',
    description: 'Experience lightning-fast rendering with our optimized WebGL2 engine, achieving 60+ FPS on modern devices.',
    gradient: 'from-yellow-500 to-orange-500',
  },
  {
    icon: Layers,
    title: '4D Gaussian Splatting',
    description: 'Advanced volumetric capture technology that preserves temporal information for dynamic scenes.',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Globe,
    title: 'WebXR Ready',
    description: 'Immersive VR and AR experiences with full support for WebXR-compatible devices and headsets.',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-level encryption and secure model hosting with Cloudflare\'s global infrastructure.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: Cpu,
    title: 'GPU Accelerated',
    description: 'Harness the full power of modern GPUs with our optimized shaders and Web Workers.',
    gradient: 'from-red-500 to-pink-500',
  },
  {
    icon: Sparkles,
    title: 'AI Enhanced',
    description: 'Intelligent compression and quality optimization powered by machine learning algorithms.',
    gradient: 'from-indigo-500 to-purple-500',
  },
  {
    icon: Gauge,
    title: 'Progressive Loading',
    description: 'Stream large models efficiently with chunk-based loading and adaptive quality settings.',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: Cloud,
    title: 'Edge Deployment',
    description: 'Global CDN distribution ensures low latency access from anywhere in the world.',
    gradient: 'from-cyan-500 to-blue-500',
  },
]

export function Features() {
  return (
    <section id="features" className="py-20 lg:py-32 relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Cutting-Edge Features
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-300 max-w-2xl mx-auto"
          >
            Built with the latest technologies to deliver unparalleled performance and quality
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <div className="h-full bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary-500/10">
                <div
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.gradient} p-2.5 mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <feature.icon className="w-full h-full text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-400">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}